import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../hooks/useLanguage';
import { useTripData, useMinimumPrice } from '../data/tripData';

interface RelatedTripsProps {
  tripIds: string[];
  title?: string;
}

export const RelatedTrips: React.FC<RelatedTripsProps> = ({ tripIds, title }) => {
  const { t } = useLanguage();

  if (!tripIds || tripIds.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm">
      <h3 className="text-xl font-semibold mb-6">
        {title || t('common.relatedTrips')}
      </h3>
      <div className="grid grid-cols-1 gap-4">
        {tripIds.map((tripId) => (
          <RelatedTripItem key={tripId} tripId={tripId} />
        ))}
      </div>
    </div>
  );
};

const RelatedTripItem: React.FC<{ tripId: string }> = ({ tripId }) => {
  const { t } = useLanguage();
  const tripData = useTripData(tripId);
  const { price, loading } = useMinimumPrice(tripId);

  if (!tripData) return null;

  const handleBookButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // The Link will handle the navigation
  };

  return (
    <Link 
      to={`/trip/${tripId}`}
      className="block group cursor-pointer"
    >
      <div className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors duration-200">
        <div className="flex flex-col md:flex-row md:items-center space-y-4 md:space-y-0 md:space-x-4">
          <div className="flex-shrink-0 w-full md:w-64 h-40 md:h-44 rounded-lg overflow-hidden">
            <img
              src={tripData.image}
              alt={tripData.title}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex-1 min-w-0 flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="pr-0 md:pr-4">
              <h4 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors duration-200">
                {tripData.title}
              </h4>
              <p className="text-sm text-gray-600 mt-1">
                {tripData.description}
              </p>
            </div>
            <div className="flex flex-col items-start md:items-end gap-3 md:ml-auto">
                <div className="inline-flex items-center px-3 py-1 rounded-md border border-gray-300 bg-white text-gray-700 text-sm">
                  <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="font-semibold">{tripData.durationDays}</span>&nbsp;{t('tripDetails.days')}
                  <span className="mx-2 text-gray-300">/</span>
                  <span className="font-semibold">{tripData.durationNights}</span>&nbsp;{t('tripDetails.nights')}
                </div>
                {!loading && price && (
                  <div className="px-3 py-2 rounded-md border border-green-200 bg-green-50 md:text-right w-full md:w-fit">
                    <div className="text-xs text-green-700">{t('common.startingFrom')}</div>
                    <div className="text-lg font-extrabold text-green-700 leading-none">
                      {price}
                    </div>
                  </div>
                )}
                <div
                  onClick={handleBookButtonClick}
                  className="bg-blue-600 text-white text-sm font-semibold px-3 py-2 rounded-md hover:bg-blue-700 transition-colors whitespace-nowrap w-full md:w-auto text-center md:text-left"
                >
                  {t('tripDetails.bookThisTrip')}
                </div>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};